import cx from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RiArrowDropRightFill } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { isUserOnboarding, setTooltipData } from '../../components/gamificationTooltip/helpers';
import ArrowDownIconV2 from '../../components/icons/ArrowDownIconV2';
import { useContextMenu } from '../../hooks/useContextMenu';
import { selectCanReplaceProp } from '../../redux/slicers/admin/curatorUnityObjectSlicer';
import { selectGamificationTooltipData } from '../../redux/slicers/gamificationTooltipSlicer';
import styles from './ContextMenu.module.scss';
import { closeSidebar } from '../../helpers/curatorActions';
import { selectIsSelectMapModeActive } from '../../redux/slicers/admin/curatorStylesSlicer';
import { throttle } from 'lodash';

export const ContextMenu = ({
  items,
  selector,
  element,
  fromCurator,
  version,
  setShow,
  unityEvent,
  disabled,
  className
}) => {
  if (!selector && !element) return null;

  return (
    <ContextMenuElement
      items={items}
      fromCurator={fromCurator}
      selector={selector}
      element={element}
      version={version}
      disabled={disabled}
      setShow={setShow}
      unityEvent={unityEvent}
      className={className}
    />
  );
};

export const ContextMenuElement = ({
  items,
  selector,
  element,
  fromCurator,
  unityEvent,
  disabled,
  setShow,
  version,
  className = ''
}) => {
  const gamificationTooltipData = useSelector(selectGamificationTooltipData);
  const { step } = gamificationTooltipData;
  const isSelectMapModeActive = useSelector(selectIsSelectMapModeActive);
  const { anchorPoint, show } = useContextMenu({
    selector,
    element,
    unityEvent,
    disabled,
  });

  useEffect(() => {
    if (!fromCurator) {
      show && setShow && setShow(false);
      return;
    }
    if (show && isSelectMapModeActive) {
      closeSidebar();
    }
    let onboardingStep = 'REPLACE_PROP';
    if (!show) {
      onboardingStep = 'SELECT_ANY_PROP';
    }
    if (isUserOnboarding() && (step === 'REPLACE_PROP' || step === 'SELECT_ANY_PROP')) {
      setTooltipData({
        step: onboardingStep,
      });
    }
  }, [fromCurator, show]);

  if (!show || (fromCurator && step !== 'REPLACE_PROP' && isUserOnboarding())) return null;

  const itemsArray = typeof items === 'function' ? items() : items;

  return <MenuUI items={itemsArray} left={anchorPoint.x} top={anchorPoint.y} version={version} className={className} />;
};

export const MenuUI = ({ left, top, refElem, items, version, otherStyles, className }) => {
  const [{ x, y }, setPosition] = useState({ x: left, y: top });
  const [submenuPositionX, setSubmenuPositionX] = useState('right');
  const [submenuPositionY, setSubmenuPositionY] = useState('top');
  const elementRef = useRef(null);
  const isV2 = version === '2';
  const canReplaceProp = useSelector(selectCanReplaceProp);
  const gamificationTooltipData = useSelector(selectGamificationTooltipData);
  const { step } = gamificationTooltipData;
  const isOnboardingState = isUserOnboarding();
  const [nestedMenuposition, setNestedMenuPosition] = useState({
    top: 0,
    left: 0,
    bottom: 'initial',
  });
  const subMenuRef = {};

  useLayoutEffect(() => {
    const element = refElem || elementRef;
    if (!element.current) return;

    let { left, top } = element.current.getBoundingClientRect();
    const menuWidth = elementRef.current.getBoundingClientRect().width;
    const menuHeight = elementRef.current.getBoundingClientRect().height;
    const width = element.current.clientWidth;
    const height = element.current.clientHeight;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const calculatedLeft = refElem
      ? width - menuWidth - 10
      : left + width > windowWidth
      ? windowWidth - width
      : left;
    const calculatedTop = refElem
      ? top + height + menuHeight < windowHeight
        ? top + height
        : top - menuHeight
      : top + height > windowHeight
      ? windowHeight - height
      : top;

    setPosition({ x: calculatedLeft, y: calculatedTop });

    // calclulate submenu position
    setSubmenuPositionX(calculatedLeft + width + 200 > windowWidth ? 'left' : 'right');

    setSubmenuPositionY(calculatedTop + height + 120 > windowHeight ? 'bottom' : 'top');
  }, [left, top, items.length]);

  const onMouseEnter = throttle(({ e, parentIndex, childIndex }) => {
    if (!isV2 || !e?.currentTarget || !subMenuRef?.[parentIndex]?.[childIndex]) {
      return;
    }
    const windowHeight = window.innerHeight;
    const { left, top, height, width } = e?.currentTarget?.getBoundingClientRect();
    const dimensions = subMenuRef?.[parentIndex]?.[childIndex]?.getBoundingClientRect();
    const menuHeight = dimensions?.height;
    setNestedMenuPosition({
      top: top + menuHeight < windowHeight ? top : top - menuHeight + height,
      left: left + width,
      bottom: 'initial',
    });
  }, 70);

  return (
    <div
      ref={elementRef}
      className={cx(
        styles.root,
        submenuPositionX === 'left' && styles.submenuLeft,
        submenuPositionY === 'bottom' && styles.submenuBottom,
        isV2 && styles.v2,
        className
      )}
      style={{
        left: x,
        top: y,
        ...otherStyles,
      }}>
      {items.map(({ id, icon, onMouseDown, label, items, disabled }, idx) => {
        return (
          <div
            className={cx(
              styles.menuItem,
              disabled && styles.disabled,
              isOnboardingState &&
                label === 'Replace Prop' &&
                step === 'REPLACE_PROP' &&
                canReplaceProp &&
                'hightlightOnboarding'
            )}
            onMouseDown={onMouseDown}
            key={idx}
            {...(id ? { id } : {})}
            ref={(elem) => {
              if (
                elem &&
                isOnboardingState &&
                canReplaceProp &&
                label === 'Replace Prop' &&
                step === 'REPLACE_PROP'
              ) {
                setTooltipData({
                  target: elem,
                });
              }
            }}>
            {icon && <span className={styles.icon}>{icon}</span>}
            {label}

            {items && !isV2 && <RiArrowDropRightFill fontSize={24} className={styles.arrow} />}
            {items && isV2 && <ArrowDownIconV2 className={cx(styles.arrow, styles.arrowV2)} />}

            {items && (
              <div className={styles.submenu}>
                <div className={cx('h-100 w-100', isV2 && styles.overflow)}>
                  {items.map((submenuItem, submenuIndex) => {
                    const { items } = submenuItem;

                    return (
                      <div
                        className={cx(
                          styles.submenuItem,
                          submenuItem.disabled && styles.disabled,
                          isV2 && 'position-static'
                        )}
                        onMouseDown={submenuItem.onMouseDown}
                        onMouseEnter={(e) =>
                          onMouseEnter({ e, parentIndex: idx, childIndex: submenuIndex })
                        }
                        key={submenuItem.label}>
                        {submenuItem?.icon && <span className={styles.icon}>{submenuItem?.icon}</span>}
                        <span className={styles.labelContainer}>{submenuItem.label}</span>

                        {items && <ArrowDownIconV2 className={cx(styles.arrow, styles.arrowV2)} />}

                        {items && (
                          <div
                            className={cx(
                              styles.submenu,
                              isV2 && styles.overflow,
                              isV2 && 'position-fixed'
                            )}
                            ref={(ref) => {
                              if (!subMenuRef?.[idx]) {
                                subMenuRef[idx] = {};
                              }
                              subMenuRef[idx][submenuIndex] = ref;
                            }}
                            style={isV2 ? nestedMenuposition : {}}>
                            {items.map((submenuItem) => {
                              return (
                                <div
                                  className={cx(
                                    styles.submenuItem,
                                    submenuItem.disabled && styles.disabled
                                  )}
                                  onMouseDown={submenuItem.onMouseDown}
                                  key={submenuItem.label}>
                                  {submenuItem?.icon && <span className={styles.icon}>{submenuItem?.icon}</span>}
                                  {submenuItem.label}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
